import type { AssessmentItem } from '@app-types';

export interface AssessmentProgress {
  percentage: number;
  evaluated: number;
  total: number;
  implemented: number;
  implementedNeedsEvidence: number;
  gaps: number;
  notApplicable: number;
  notAssessed: number;
  remediated: number;
  compliant: number;
}

const EMPTY_PROGRESS: AssessmentProgress = {
  percentage: 0,
  evaluated: 0,
  total: 0,
  implemented: 0,
  implementedNeedsEvidence: 0,
  gaps: 0,
  notApplicable: 0,
  notAssessed: 0,
  remediated: 0,
  compliant: 0
};

/**
 * Assessment Progress is a compliance score (Bug #1138):
 * (Implemented + Remediated + Not Applicable) / total × 100.
 * Evaluation completion (everything except Not Assessed) is still returned as `evaluated`
 * for Publish Assessment gating.
 */
export function calculateAssessmentProgress(
  items: AssessmentItem[] | undefined,
  totalItems: number
): AssessmentProgress {
  if (!items || totalItems === 0) {
    return EMPTY_PROGRESS;
  }

  const implemented = items.filter((item) => item.evaluation === 'Implemented').length;
  const implementedNeedsEvidence = items.filter(
    (item) => item.evaluation === 'Implemented (Needs Evidence)'
  ).length;
  const gaps = items.filter((item) => item.evaluation === 'Gap').length;
  const notApplicable = items.filter((item) => item.evaluation === 'Not Applicable').length;
  const notAssessed = items.filter((item) => item.evaluation === 'Not Assessed').length;
  const remediated = items.filter((item) => item.evaluation === 'Remediated').length;

  const evaluated = implemented + implementedNeedsEvidence + gaps + notApplicable + remediated;
  const compliant = implemented + remediated + notApplicable;
  const percentage = Math.round((compliant / totalItems) * 100);

  return {
    percentage,
    evaluated,
    total: totalItems,
    implemented,
    implementedNeedsEvidence,
    gaps,
    notApplicable,
    notAssessed,
    remediated,
    compliant
  };
}
